import React, {useRef, useState} from "react";
import {Alert, CircularProgress, IconButton, Paper, Stack, TextField, Typography, Box} from "@mui/material";
import {CloudUploadIcon} from "lucide-react";
import apiService from "../services/apiService";

/**
 * A universal, reusable component for uploading an image to a specified path in GCS.
 * It is fully controlled by its parent component through props.
 */
const ImageUploader = ({
                           imageUrl,           // The current image URL to display
                           onImageUrlChange,   // A function to call when the URL changes (e.g., after upload)
                           uploadPath,         // The destination path in GCS (e.g., "Prizes", "config/rewards")
                           baseFileName,       // The name to use as a base for the uploaded file (e.g., the prize name)
                           isUploadEnabled = true // A boolean to control if the upload button is active
                       }) => {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const fileInputRef = useRef(null);

    const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.size > MAX_FILE_SIZE_BYTES) {
            setUploadError('File is too large. Please select an image under 10MB.');
            if (fileInputRef.current) fileInputRef.current.value = ''; // Reset file input
            return;
        }

        setIsUploading(true);
        setUploadError(null);

        try {
            // 1. The filename logic is now generic, using the baseFileName prop.
            const timestamp = Date.now();
            const cleanBaseName = baseFileName.replace(/[^a-zA-Z0-9]/g, '') || 'image';
            const fileExtension = file.name.split('.').pop();
            const finalFileName = `${cleanBaseName}-${timestamp}.${fileExtension}`;

            const formData = new FormData();
            formData.append('file', file);
            formData.append('fileName', finalFileName);
            // The GCS path is now completely controlled by the uploadPath prop.
            formData.append('path', uploadPath || '');

            const response = await apiService.post('/api/admin/files/upload', formData);

            // 3. Instead of setting state directly, we call the parent's function.
            onImageUrlChange(response.url);

        } catch (err) {
            setUploadError(err.message || 'Upload failed.');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = ''; // Reset file input
        }
    };

    return (
        <Paper sx={{p: 2}}>
            <Typography variant="h6" gutterBottom>Image</Typography>
            <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                        fullWidth
                        size="small"
                        label="Image URL"
                        value={imageUrl}
                        // 4. Use the onImageUrlChange callback for manual edits too.
                        onChange={e => onImageUrlChange(e.target.value)}
                    />
                    <IconButton
                        component="label"
                        // 5. The button is now controlled by the isUploadEnabled prop.
                        disabled={isUploading || !isUploadEnabled}
                        color="primary"
                    >
                        {isUploading ? <CircularProgress size={24}/> : <CloudUploadIcon/>}
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            accept="image/png, image/jpeg, image/webp"
                            onChange={handleFileSelect}
                        />
                    </IconButton>
                </Stack>

                {!isUploadEnabled && <Typography variant="caption" color="text.secondary">
                    Enter an item name to enable upload.
                </Typography>}
                {uploadError && <Alert severity="error" sx={{mt: 1}}>{uploadError}</Alert>}

                {imageUrl && (
                    <Box
                        component="img"
                        src={imageUrl}
                        sx={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: 600,
                            objectFit: 'contain',
                            borderRadius: 1,
                        }}
                    />
                )}
            </Stack>
        </Paper>
    );
};

export default ImageUploader;